import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as Lock } from '../../assets/img/icn-lock.svg';
import { ReactComponent as LogOut } from '../../assets/img/icn-logout.svg';
import { ReactComponent as IconArrow } from '../../assets/img/icn-arrow.svg';
import { ReactComponent as IconPortfolio } from '../../assets/img/icn-portfolio.svg';
import { ReactComponent as IconBrush } from '../../assets/img/icn-brush.svg';
import { ReactComponent as IconPhoto } from '../../assets/img/icn-photo.svg';
import { ReactComponent as IconDamage } from '../../assets/img/icn-damage.svg';
import { ReactComponent as IconSettings } from '../../assets/img/icn-settings.svg';
import { ReactComponent as IconPdf } from '../../assets/img/icn-pdf.svg';
import IconWrench from '../../assets/img/IconWrench';

import { PasswordModal } from '../../containers';
import { Footer, Text, Menu, ActionsDropDown, Container, EnvBadge } from '../../components';
import { actions as sessionActions, constants as sessionConstants, selectors as sessionSelectors } from '../../redux/session';
import { actions as customerActions, selectors as customerSelectors } from '../../redux/customers';
import { actions as subscriptionActions } from '../../redux/subscriptions/index';
import { actions as customerConfigurationsActions, selectors as customerConfigurationsSelectors } from '../../redux/customerConfigurations';
import { authenticationService } from '../../services';
import { makeGet, makePost } from '../../api/ajax';
import { history } from '../../utils/history';
import { __local__, isDev } from '../../utils/constants';
import { routes } from '../../routes/Routes';
import { trackEvent, EVENT_TYPES } from '../../api/analytics';

import './sass/menu-page.scss';

const {
  EDIT_CUSTOMER_CONFIGURATION, EDIT_WORKFLOWS, VIEW_VEHICLES, EDIT_TRANSLATIONS,
  IMPORT_PHOTO_SERIES, EDIT_BLACKLISTED_USER_AGENT, VIEW_CUSTOM_DAMAGE_TYPES, EDIT_CUSTOM_IMAGE_TYPE, VIEW_STATISTICS,
  VIEW_WINDSHIELD_REPAIRABILITY_PROFILES
} = sessionConstants.PERMISSION_TYPES;

const getCustomizationClassnames = (currentRoute) => {
  let customizationClassNames = 'nav-item';

  if ([routes.customDamageTypes, routes.translations].indexOf(currentRoute) !== -1) {
    customizationClassNames += ' active';
  }

  return customizationClassNames;
};

const getAdministrationClassnames = (currentRoute) => {
  let administrationClassNames = 'nav-item';

  if ([routes.userAgentBlacklist, routes.photoSeriesImport, routes.statistics].indexOf(currentRoute) !== -1) {
    administrationClassNames += ' active';
  }

  return administrationClassNames;
};

const renderCustomizationMenuItem = (hasPermission, currentRoute) => (
  <li className={getCustomizationClassnames(currentRoute)}>
    <ActionsDropDown toggle={<IconArrow />} toggleText="Customization">
      {hasPermission(EDIT_WORKFLOWS) && (
        <ActionsDropDown.Item href={routes.workflows}>
          <IconPortfolio />
          <p className="p-3">
            Workflows
          </p>
        </ActionsDropDown.Item>)}
      {hasPermission(EDIT_CUSTOMER_CONFIGURATION) && (
        <ActionsDropDown.Item href={routes.branding}>
          <IconBrush />
          <p className="pl-3">
            Branding
          </p>
        </ActionsDropDown.Item>)}
      {hasPermission(EDIT_CUSTOM_IMAGE_TYPE) && (
        <ActionsDropDown.Item href={routes.customImageTypes}>
          <IconPhoto />
          <p className="pl-3">
            Image types
          </p>
        </ActionsDropDown.Item>)}
      {hasPermission(VIEW_CUSTOM_DAMAGE_TYPES) && (
        <ActionsDropDown.Item href={routes.customDamageTypes}>
          <IconDamage />
          <p className="pl-3">
            Damages
          </p>
        </ActionsDropDown.Item>)}
      {hasPermission(VIEW_WINDSHIELD_REPAIRABILITY_PROFILES) && (
        <ActionsDropDown.Item href={routes.windshieldRepairabilityProfiles}>
          <IconWrench color="blue" />
          <p className="pl-3">
            Repairability profiles
          </p>
        </ActionsDropDown.Item>
      )}
      {hasPermission(EDIT_TRANSLATIONS) && (
        <ActionsDropDown.Item href={routes.translations}>
          <IconSettings />
          <p className="pl-3">
            Translations
          </p>
        </ActionsDropDown.Item>)}
    </ActionsDropDown>
  </li>
);

const renderAdministrationMenuItem = (hasPermission, isGlobalConfEditCustomer, currentRoute) => (
  <li className={getAdministrationClassnames(currentRoute)}>
    <ActionsDropDown toggle={<IconArrow />} toggleText="Administration">
      {isGlobalConfEditCustomer && hasPermission(EDIT_BLACKLISTED_USER_AGENT) && (
        <ActionsDropDown.Item href={routes.userAgentBlacklist}>
          <IconSettings />
          <p className="pl-3">
            User agent blacklist
          </p>
        </ActionsDropDown.Item>)}
      {isGlobalConfEditCustomer && hasPermission(IMPORT_PHOTO_SERIES) && (
        <ActionsDropDown.Item href={routes.photoSeriesImport}>
          <IconPhoto />
          <p className="pl-3">
            Import photo series
          </p>
        </ActionsDropDown.Item>)}
      {isGlobalConfEditCustomer && hasPermission(VIEW_STATISTICS) && (
        <ActionsDropDown.Item href={routes.statistics}>
          <IconPdf />
          <p className="pl-3">
            Statistics
          </p>
        </ActionsDropDown.Item>)}
    </ActionsDropDown>
  </li>
);

const MenuPage = ({ currentRoute, children, hideMenu = false }) => {
  const [modalShow, setModalShow] = useState(false);

  const dispatch = useDispatch();
  const customer = useSelector(customerSelectors.selectCurrentCustomer);
  const isGlobalConfEditCustomer = useSelector(sessionSelectors.getIsGlobalConfEditCustomer);
  const customerConfigurationSimple = useSelector(customerConfigurationsSelectors.selectCustomerConfigurationSimple);

  // customer dispatcher useEffect
  useEffect(() => {
    if (customer) return;

    makeGet('customers/current').subscribe(({ response }) => dispatch(customerActions.loadCustomerSuccess(response)));
  }, [customer, dispatch]);

  // customer configuration dispatcher useEffect
  useEffect(() => {
    if (customerConfigurationSimple) return;

    makeGet('customerConfigurations/current/simple').subscribe(({ response }) => {
      dispatch(customerConfigurationsActions.loadCurrentCustomerConfigurationSimpleSuccess(response));
    });
  }, [customerConfigurationSimple, dispatch]);

  useEffect(() => {
    if (!customerConfigurationSimple) return;

    dispatch(subscriptionActions.loadEnabledFeaturesSuccess(customerConfigurationSimple.enabledFeatures));
  }, [dispatch, customerConfigurationSimple]);

  const handleLogOutClick = () => {
    makePost('session/logout', {}).subscribe(() => {
      // Track event in Analytics
      trackEvent(EVENT_TYPES.USER_LOGGED_OUT);

      authenticationService.logout();

      dispatch(sessionActions.logOut());

      history.push(routes.login);
    });
  };

  if (!customer) return null;

  const { hasPermission } = authenticationService;

  const hasCustomizationPermission = hasPermission(EDIT_WORKFLOWS) || hasPermission(EDIT_CUSTOMER_CONFIGURATION)
    || hasPermission(VIEW_CUSTOM_DAMAGE_TYPES) || hasPermission(EDIT_CUSTOM_IMAGE_TYPE) || hasPermission(EDIT_TRANSLATIONS);

  const hasAdministrationPermission = isGlobalConfEditCustomer && (hasPermission(EDIT_BLACKLISTED_USER_AGENT)
    || hasPermission(IMPORT_PHOTO_SERIES)
    || hasPermission(VIEW_STATISTICS));

  return (
    <div className="menu-page">
      <Menu>
        {/* Only public users do not have VIEW_VEHICLES permission */}
        {!hideMenu && hasPermission(VIEW_VEHICLES) && (
          <ul className="navbar-nav">
            <Menu.MenuItem name="Dashboard" route={routes.home} currentRoute={currentRoute} />
            <Menu.MenuItem name="Vehicles" route={routes.vehicles} currentRoute={currentRoute} />
            {hasPermission(EDIT_CUSTOMER_CONFIGURATION)
            && <Menu.MenuItem name="Account" route={routes.account} currentRoute={currentRoute} />}
            {hasCustomizationPermission && renderCustomizationMenuItem(hasPermission, currentRoute)}
            {hasAdministrationPermission && renderAdministrationMenuItem(hasPermission, isGlobalConfEditCustomer, currentRoute)}
          </ul>
        )}

        <div className="ml-auto">
          <ActionsDropDown toggle={<IconArrow />} toggleText={customer.name}>
            {/* Only public users don't have editPhotoSeries permission */}
            {hasPermission(VIEW_VEHICLES) && (
              <ActionsDropDown.Item href={routes.settings}>
                <IconSettings />
                <span className="pl-3">Settings</span>
              </ActionsDropDown.Item>
            )}

            {hasPermission(VIEW_VEHICLES) && (
              <ActionsDropDown.Item onClick={() => setModalShow(true)}>
                <Lock />
                <span className="pl-3">Change password</span>
              </ActionsDropDown.Item>
            )}

            {authenticationService.currentUserValue?.representatives
              && authenticationService.currentUserValue.representatives.filter((rep) => !rep.isHome).length > 0 && (
              <ActionsDropDown.Item href={routes.representative}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 21H21M5 21V7L13 3V21M19 21V11L13 7M9 9V9.01M9 12V12.01M9 15V15.01M9 18V18.01"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span className="pl-3">Change company</span>
              </ActionsDropDown.Item>
            )}

            <ActionsDropDown.Item onClick={() => handleLogOutClick()}>
              <LogOut />

              <span className="pl-3">Log out</span>
            </ActionsDropDown.Item>
          </ActionsDropDown>
        </div>
      </Menu>

      {isDev && <EnvBadge isLocalhost={__local__} />}

      <div>
        <PasswordModal show={modalShow} onModalClose={() => setModalShow(false)} />
      </div>

      <Container>
        <div className="row">
          <div className="col-sm">
            <div className={hideMenu ? '' : `content-wrapper${isDev ? ' content-wrapper--with-env-badge' : ''}`}>{children}</div>
            {!hideMenu && (
              <Footer>
                <Text align="center">DriveX Technologies &copy;2021 All Rights Reserved</Text>
              </Footer>
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

MenuPage.propTypes = {
  children: PropTypes.node,
  currentRoute: PropTypes.string,
  hideMenu: PropTypes.bool,
};

export default MenuPage;
